import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { JwtHelperService, JwtInterceptor } from '@auth0/angular-jwt'
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store'

import * as fromAuth from '../../auth/reducers'
import * as auth from './../../auth/actions/auth.actions'

import { AuthService } from '../../auth/services/auth.service'


type CurriedHandler = (res: HttpErrorResponse) => Observable<HttpEvent<any>>

const omitPredicate = (model: {url: string}) => {
  return model.url.endsWith('auth/login')
    || model.url.endsWith('auth/token')
    || model.url.endsWith('/settings')
    || model.url.endsWith('/translations')
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  authenticating = false
  done$ = new Subject<boolean>()
  isLoggedIn = new BehaviorSubject<boolean>(false)
  refreshToken: string
  token: string

  _authSvc: AuthService
  _jwtHelper: JwtHelperService
  _jwtInterceptor: JwtInterceptor

  constructor(
    private store: Store<any>,
    private router: Router,
    private injector: Injector
  ) {
    store.pipe(select(fromAuth.getLoggedIn)).subscribe(this.isLoggedIn)
    store.pipe(select(fromAuth.getRefreshtoken)).subscribe(token => this.refreshToken = token)
    store.pipe(select(fromAuth.getToken)).subscribe(token => this.token = token)
    this.done$.subscribe(() => this.authenticating = false)
  }

  get authentication(): AuthService {
    if(!this._authSvc)
      this._authSvc = this.injector.get(AuthService)
    return this._authSvc
  }

  get jwtHelper(): JwtHelperService {
    if (!this._jwtHelper)
      this._jwtHelper = this.injector.get(JwtHelperService)
    return this._jwtHelper
  }

  get jwtInterceptor(): JwtInterceptor {
    if (!this._jwtInterceptor)
      this._jwtInterceptor = this.injector.get(JwtInterceptor)
    return this._jwtInterceptor
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authHeader = request.headers.get('authorization')
    if (authHeader && !omitPredicate(request)) {
      const currentTokenExpirationDate = authHeader
        ? this.jwtHelper.getTokenExpirationDate(authHeader.split('JWT ')[1])
        : null
      const currentDate = new Date()
      currentDate.setMinutes(currentDate.getMinutes() + 1)

      if (!!currentTokenExpirationDate && (currentTokenExpirationDate <= currentDate)) {
        return this.useRefreshToken(request, next)
      } else {
        request = request.clone({
          setHeaders: {
            'Authorization': `JWT ${this.token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
          }
        })
        return next.handle(request)
          .pipe(
            catchError((e: HttpErrorResponse) => {
              if (this.isLoggedIn.getValue() && e.status === 401)
                this.store.dispatch(new auth.Logout())
              return throwError(e)
            })
          )
      }
    } else {
      return next.handle(request)
        .pipe(
          catchError((e: HttpErrorResponse) => {
            if (this.isLoggedIn.getValue() && e.status === 401)
              this.store.dispatch(new auth.Logout())
            return throwError(e)
          })
        )
    }
  }

  private useRefreshToken(request: HttpRequest<any>, next: HttpHandler) : Observable<HttpEvent<any>> {
    if (this.authenticating) {
      return this.done$
        .pipe(
          take(1),
          filter(success => !!success),
          switchMap(_ => {
            request = request.clone({
              setHeaders: {
                'Authorization': `JWT ${this.token}`,
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
              }
            })
            return next.handle(request)
            }
          )
        )
    }

  this.authenticating = true
  return this.authentication.refreshToken(this.refreshToken)
    .pipe(
      switchMap(data => {
        this.token = data && data.token
        this.done$.next(true)
        request = request.clone({
          setHeaders: {
            'Authorization': `JWT ${this.token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
          }
        })
        return next.handle(request)
      }),
      catchError((e: HttpErrorResponse) => {
        if (this.isLoggedIn.getValue() && e.status === 401 && e.url.indexOf('token') !== -1)
          this.store.dispatch(new auth.Logout())
        this.done$.next(false)
        return throwError(e)
      })
    )
  }
}
